/**
 * SolicitudDocCreateModal — el perito interno solicita documentación.
 *
 * Permite elegir destinatario (asegurado / perjudicado / reparador) y
 * describir qué documentos se necesitan. Al confirmar crea la solicitud
 * que entrará en la cola de los colectores.
 */
import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X } from "@phosphor-icons/react";

const TARGETS = [
  { k: "asegurado", label: "Asegurado" },
  { k: "perjudicado", label: "Perjudicado" },
  { k: "reparador", label: "Reparador" },
];

export default function SolicitudDocCreateModal({ expediente, onClose, onCreated }) {
  const [targetType, setTargetType] = useState("asegurado");
  const [targetImplicadoId, setTargetImplicadoId] = useState("");
  const [targetReparadorId, setTargetReparadorId] = useState("");
  const [reparadores, setReparadores] = useState([]);
  const [descripcion, setDescripcion] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const perjudicados = (expediente?.implicados || []).filter(
    (i) => (i.tipo || "").toLowerCase() === "perjudicado" || i.es_perjudicado,
  );

  useEffect(() => {
    if (targetType !== "reparador") return;
    api.get("/reparadores").then((r) => setReparadores(r.data || [])).catch(() => setReparadores([]));
  }, [targetType]);

  const submit = async () => {
    const desc = descripcion.trim();
    if (!desc) {
      toast.error("Indica qué documentos necesitas");
      return;
    }
    if (targetType === "perjudicado" && !targetImplicadoId) {
      toast.error("Selecciona el perjudicado");
      return;
    }
    if (targetType === "reparador" && !targetReparadorId) {
      toast.error("Selecciona el reparador");
      return;
    }
    setSubmitting(true);
    try {
      const label = (() => {
        if (targetType === "asegurado") return expediente?.extracted_data?.asegurado_nombre || "Asegurado";
        if (targetType === "perjudicado") {
          const p = perjudicados.find((x) => x.id === targetImplicadoId);
          return p ? (p.nombre || "Perjudicado") : "Perjudicado";
        }
        const r = reparadores.find((x) => x.id === targetReparadorId);
        return r ? (r.nombre || r.name || "Reparador") : "Reparador";
      })();
      await api.post(`/expedientes/${expediente.id}/solicitudes-doc`, {
        target_type: targetType,
        target_implicado_id: targetType === "perjudicado" ? targetImplicadoId : null,
        target_reparador_id: targetType === "reparador" ? targetReparadorId : null,
        target_label: label,
        descripcion: desc,
      });
      toast.success("Solicitud enviada al equipo");
      onCreated?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo crear la solicitud");
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center p-3"
      onClick={onClose}
      data-testid="solicitud-doc-modal"
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-5 py-3 border-b border-border flex items-center justify-between">
          <h2 className="font-display font-bold text-base">Solicitar documentación</h2>
          <button onClick={onClose} className="text-muted hover:text-foreground" data-testid="solicitud-doc-close">
            <X className="w-4 h-4" weight="bold" />
          </button>
        </header>

        <div className="p-5 space-y-4">
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-muted">Pedir a</label>
            <div className="grid grid-cols-3 gap-1 mt-1">
              {TARGETS.map((t) => (
                <button
                  key={t.k}
                  type="button"
                  onClick={() => setTargetType(t.k)}
                  data-testid={`solicitud-doc-target-${t.k}`}
                  className={`text-[12.5px] font-semibold px-2.5 py-2 rounded-md border transition-all ${
                    targetType === t.k
                      ? "bg-brand text-white border-brand"
                      : "bg-white text-foreground border-border hover:border-brand"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {targetType === "perjudicado" && (
            <div data-testid="solicitud-doc-perjudicado-select">
              <label className="text-[11px] font-bold uppercase tracking-wider text-muted">¿Cuál perjudicado?</label>
              {perjudicados.length === 0 ? (
                <div className="text-[12px] text-muted bg-zinc-50 border border-border rounded-md px-3 py-2 mt-1 italic">
                  No hay perjudicados registrados en este expediente.
                </div>
              ) : (
                <select
                  value={targetImplicadoId}
                  onChange={(e) => setTargetImplicadoId(e.target.value)}
                  className="!text-sm mt-1"
                >
                  <option value="">— Elige —</option>
                  {perjudicados.map((p) => (
                    <option key={p.id} value={p.id}>{p.nombre || "(sin nombre)"}</option>
                  ))}
                </select>
              )}
            </div>
          )}

          {targetType === "reparador" && (
            <div data-testid="solicitud-doc-reparador-select">
              <label className="text-[11px] font-bold uppercase tracking-wider text-muted">¿Qué reparador?</label>
              <select
                value={targetReparadorId}
                onChange={(e) => setTargetReparadorId(e.target.value)}
                className="!text-sm mt-1"
              >
                <option value="">— Elige —</option>
                {reparadores.map((r) => (
                  <option key={r.id} value={r.id}>{r.nombre || r.name}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-muted">
              Documentación a solicitar
            </label>
            <textarea
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              rows={5}
              placeholder="Ej. Facturas originales con desglose por partidas, fotos del depósito antes y después, justificante de propiedad del vehículo..."
              data-testid="solicitud-doc-descripcion"
              className="!text-sm mt-1 w-full"
              autoFocus
            />
            <div className="text-[10.5px] text-muted mt-1">
              Sé concreto: cuanto más claro lo expliques, más rápido lo conseguirá el equipo.
            </div>
          </div>
        </div>

        <footer className="px-5 py-3 border-t border-border flex justify-end gap-2 bg-zinc-50">
          <button
            onClick={onClose}
            className="text-[12px] font-semibold px-3 py-2 rounded-md text-muted hover:bg-zinc-100"
            data-testid="solicitud-doc-cancel"
          >
            Cancelar
          </button>
          <button
            onClick={submit}
            disabled={submitting || !descripcion.trim()}
            data-testid="solicitud-doc-submit"
            className="bg-brand hover:bg-brand/90 text-white text-[12.5px] font-semibold px-4 py-2 rounded-md disabled:opacity-50"
          >
            {submitting ? "Enviando…" : "Enviar solicitud"}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
